import { useState, useEffect } from "react";
import { PlusCircle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { VehicleFields } from "@/components/VehicleFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const Field = ({ label, value, onChange, testid, type = "text", required }) => (
  <div>
    <Label className="text-xs">
      {label} {required && <span className="text-crimson-600">*</span>}
    </Label>
    <Input
      type={type}
      className="mt-1.5 min-h-[44px]"
      data-testid={testid}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const StudioSelect = ({ studios, value, onChange }) => (
  <div>
    <Label className="text-xs">Studio</Label>
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
      <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="free-entry-studio">
        <SelectValue placeholder="Optional" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">No studio</SelectItem>
        {studios.map((s) => (
          <SelectItem key={s.id} value={s.id}>
            {s.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const today = () => new Date().toISOString().slice(0, 10);

const PPF_WARRANTY_TYPES = ["5 Year", "7 Year", "10 Year"];
const SUNFILM_WARRANTY_TYPES = ["VLT 70%", "VLT 50%", "Custom VLT"];
const monthsFromType = (t) => (t === "10 Year" ? 120 : t === "7 Year" ? 84 : 60);

export const FreeEntryButton = ({ kind, studios, onDone, lockProduct = null }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [products, setProducts] = useState([]);
  const [detailers, setDetailers] = useState([]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const isWarranty = kind === "warranty";
  const isSunfilm = f.product_type === "Velotint Sunfilm";

  useEffect(() => {
    if (open && isWarranty) {
      api.get("/warranty/products").then(({ data }) => setProducts(data || [])).catch(() => setProducts([]));
      api.get("/warranties/detailer-options").then(({ data }) => setDetailers(data || [])).catch(() => setDetailers([]));
    }
  }, [open, isWarranty]);

  const onProductChange = (name) => {
    const p = products.find((x) => x.name === name);
    const pt = p?.product_type || "PPF";
    const sf = pt === "Velotint Sunfilm";
    setF((s) => ({
      ...s, product_name: name, product_type: pt,
      warranty_type: sf ? SUNFILM_WARRANTY_TYPES[0] : PPF_WARRANTY_TYPES[0],
      duration_months: sf ? s.duration_months : String(monthsFromType(PPF_WARRANTY_TYPES[0])),
    }));
  };

  const onWarrantyTypeChange = (t) => {
    setF((s) => ({
      ...s, warranty_type: t,
      duration_months: s.product_type === "Velotint Sunfilm" ? s.duration_months : String(monthsFromType(t)),
    }));
  };

  const openDialog = () => {
    setF(
      isWarranty
        ? {
            start_date: today(), duration_months: "60", product_type: lockProduct || "PPF",
            product_name: "", warranty_type: "5 Year", studio_name: "",
            veh: { make: "", model: "", colour: "", plate: "" },
          }
        : { start_date: today(), duration_months: "24", body_parts_covered: "Full Body" }
    );
    setOpen(true);
  };

  const submit = async () => {
    if (!f.customer_name?.trim()) return toast.error("Customer name is required");
    if (isWarranty && !f.product_name?.trim()) return toast.error("Select a product");
    if (isWarranty && !f.warranty_type?.trim()) return toast.error("Warranty type is required");
    if (!f.start_date) return toast.error("Registration date is required");
    setBusy(true);
    try {
      if (isWarranty) {
        const veh = f.veh || {};
        const vehicleStr = [veh.make, veh.model, veh.colour].filter(Boolean).join(" ");
        await api.post("/warranties/standalone", {
          customer_name: f.customer_name,
          contact: f.contact || "",
          email: f.email || "",
          studio_email: f.studio_email || "",
          detailer_id: f.detailer_id || null,
          detailer_name: f.detailer_id ? (f.studio_name || "") : "",
          studio_id: f.studio_id || null,
          studio_name: f.studio_name || "",
          warranty_type: f.warranty_type,
          product_type: f.product_type || "PPF",
          product_name: f.product_name || "",
          service: f.service || "",
          serial_number: f.serial_number || "",
          rolls: (f.rolls || []).filter((r) => (r.serial_number || "").trim()),
          certificate_no: f.certificate_no || "",
          start_date: f.start_date,
          duration_months: Number(f.duration_months || 60),
          vehicle: vehicleStr,
          brand: veh.make || "",
          model: veh.model || "",
          colour: veh.colour || "",
          registration_no: veh.plate || "",
          chassis_no: f.chassis_no || "",
          area_sqft: f.area_sqft ? Number(f.area_sqft) : null,
          panels_coverage: f.panels_coverage || "Full",
          notes: f.notes || "",
        });
      } else {
        await api.post("/protection-plans/standalone", {
          customer_name: f.customer_name,
          contact: f.contact || "",
          email: f.email || "",
          studio_id: f.studio_id || null,
          vehicle_model: f.vehicle_model || "",
          registration_no: f.registration_no || "",
          chassis_no: f.chassis_no || "",
          serial_number: f.serial_number || "",
          area_sqft_covered: Number(f.area_sqft_covered || 0),
          body_parts_covered: f.body_parts_covered || "Full Body",
          certificate_number: f.certificate_number || "",
          start_date: f.start_date,
          duration_months: Number(f.duration_months || 24),
          payment_amount: f.payment_amount === "" || f.payment_amount == null ? null : Number(f.payment_amount),
          notes: f.notes || "",
        });
      }
      toast.success(isWarranty ? "Warranty saved" : "Protection Plan saved");
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        className="min-h-[44px] border-amber-300 text-amber-700 hover:bg-amber-50"
        data-testid={`free-entry-open-${kind}${lockProduct === "Velotint Sunfilm" ? "-velotint" : ""}`}
        onClick={openDialog}
      >
        <PlusCircle className="mr-2 h-4 w-4" /> Free entry
        {isWarranty ? (lockProduct === "Velotint Sunfilm" ? " — Velotint" : " — PPF") : ""} (no job)
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid={`free-entry-dialog-${kind}`}>
          <DialogHeader>
            <DialogTitle>
              Free-entry {isWarranty ? "warranty" : "Protection Plan"} — no job required
            </DialogTitle>
          </DialogHeader>
          <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Standalone historical record. Type any values freely and set the real registration date —
            it won't affect the current month's metrics.
          </p>

          {isWarranty ? (
            <div className="space-y-5">
              {/* Owner details */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Owner name" required value={f.customer_name} onChange={(v) => set("customer_name", v)} testid="free-entry-customer-name" />
                <Field label="Phone number" value={f.contact} onChange={(v) => set("contact", v)} testid="free-entry-contact" />
                <Field label="Email (certificate is sent here)" value={f.email} onChange={(v) => set("email", v)} testid="free-entry-email" />
              </div>

              {/* Installing detailer / studio */}
              <div>
                <Label className="text-xs">Non-accredited detailer</Label>
                <Select
                  value={f.detailer_id || "manual"}
                  onValueChange={(v) => {
                    if (v === "manual") {
                      setF((s) => ({ ...s, detailer_id: "", studio_name: "" }));
                    } else {
                      const d = detailers.find((x) => x.id === v);
                      setF((s) => ({ ...s, detailer_id: v, studio_name: d?.name || "" }));
                    }
                  }}
                >
                  <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="free-entry-detailer-select">
                    <SelectValue placeholder="Select a detailer" />
                  </SelectTrigger>
                  <SelectContent>
                    {detailers.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}{d.city ? ` · ${d.city}` : ""}
                      </SelectItem>
                    ))}
                    <SelectItem value="manual">Not listed — type manually</SelectItem>
                  </SelectContent>
                </Select>
                {!f.detailer_id && (
                  <p className="mt-1 text-[11px] text-slate-500">
                    Pick a non-accredited detailer, or choose "type manually" and enter the name below.
                  </p>
                )}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field
                  label={f.detailer_id ? "Detailer / studio name" : "Installing detailer / studio (type manually)"}
                  value={f.studio_name}
                  onChange={(v) => set("studio_name", v)}
                  testid="free-entry-detailer-name"
                />
                <StudioSelect studios={studios} value={f.studio_id} onChange={(v) => set("studio_id", v)} />
                <Field label="Studio email (optional — sent a copy)" value={f.studio_email} onChange={(v) => set("studio_email", v)} testid="free-entry-studio-email" />
              </div>

              {/* Vehicle */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <VehicleFields testidPrefix="free-entry-veh" value={f.veh || {}} onChange={(veh) => set("veh", veh)} />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label="Chassis number" value={f.chassis_no} onChange={(v) => set("chassis_no", v)} testid="free-entry-chassis" />
                <Field label="Film area (sq. ft.)" type="number" value={f.area_sqft} onChange={(v) => set("area_sqft", v)} testid="free-entry-sqft" />
                <div>
                  <Label className="text-xs">Coverage</Label>
                  <Select value={f.panels_coverage || "Full"} onValueChange={(v) => set("panels_coverage", v)}>
                    <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="free-entry-coverage"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Full">Full Body</SelectItem>
                      <SelectItem value="Partial">Partial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Product + warranty type */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs">Product <span className="text-crimson-600">*</span></Label>
                  <Select value={f.product_name || ""} onValueChange={onProductChange}>
                    <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="free-entry-product">
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent>
                      {products
                        .filter((p) =>
                          !lockProduct
                            ? true
                            : (p.product_type === "Velotint Sunfilm") ===
                              (lockProduct === "Velotint Sunfilm"))
                        .map((p) => (
                        <SelectItem key={p.id} value={p.name}>
                          {p.name} · {p.product_type === "Velotint Sunfilm" ? "Velotint" : "Veloskin"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Warranty type <span className="text-crimson-600">*</span></Label>
                  <Select value={f.warranty_type || ""} onValueChange={onWarrantyTypeChange}>
                    <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="free-entry-warranty-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(isSunfilm ? SUNFILM_WARRANTY_TYPES : PPF_WARRANTY_TYPES).map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="-mt-2 text-xs text-slate-500" data-testid="free-entry-card-hint">
                {isSunfilm ? "A Velotint Sunfilm warranty card will be generated."
                          : "A Veloskin PPF warranty card will be generated."}
              </p>

              {/* Film rolls (multiple serials) + certificate */}
              <div className="rounded-md border border-brand-line p-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold">Film rolls / serial numbers</Label>
                  <Button type="button" size="sm" variant="outline" data-testid="free-entry-add-roll"
                    onClick={() => set("rolls", [...(f.rolls || []), { serial_number: "", roll_size: "" }])}>
                    <PlusCircle className="mr-1.5 h-4 w-4" /> Add Another Roll
                  </Button>
                </div>
                <div className="mt-2 space-y-2">
                  {(f.rolls && f.rolls.length ? f.rolls : [{ serial_number: "", roll_size: "" }]).map((r, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2">
                      <Input className="min-h-[40px] flex-1" placeholder="Roll serial number" data-testid={`free-entry-roll-serial-${i}`}
                        value={r.serial_number || ""}
                        onChange={(e) => { const rolls = [...(f.rolls && f.rolls.length ? f.rolls : [{ serial_number: "", roll_size: "" }])]; rolls[i] = { ...rolls[i], serial_number: e.target.value }; set("rolls", rolls); }} />
                      <Input className="min-h-[40px] w-32" placeholder="Roll size" data-testid={`free-entry-roll-size-${i}`}
                        value={r.roll_size || ""}
                        onChange={(e) => { const rolls = [...(f.rolls && f.rolls.length ? f.rolls : [{ serial_number: "", roll_size: "" }])]; rolls[i] = { ...rolls[i], roll_size: e.target.value }; set("rolls", rolls); }} />
                      {(f.rolls || []).length > 1 && (
                        <Button type="button" size="sm" variant="ghost" className="text-rose-600" data-testid={`free-entry-roll-remove-${i}`}
                          onClick={() => set("rolls", f.rolls.filter((_, x) => x !== i))}>Remove</Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <Field label="Certificate no. (blank = auto)" value={f.certificate_no} onChange={(v) => set("certificate_no", v)} testid="free-entry-cert" />

              {/* Registration date + duration */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Registration date" required type="date" value={f.start_date} onChange={(v) => set("start_date", v)} testid="free-entry-date" />
                <Field label="Duration (months)" type="number" value={f.duration_months} onChange={(v) => set("duration_months", v)} testid="free-entry-months" />
              </div>

              <Field label="Notes" value={f.notes} onChange={(v) => set("notes", v)} testid="free-entry-notes" />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Customer name" required value={f.customer_name} onChange={(v) => set("customer_name", v)} testid="free-entry-customer-name" />
              <Field label="Contact" value={f.contact} onChange={(v) => set("contact", v)} testid="free-entry-contact" />
              <Field label="Email" value={f.email} onChange={(v) => set("email", v)} testid="free-entry-email" />
              <StudioSelect studios={studios} value={f.studio_id} onChange={(v) => set("studio_id", v)} />
              <Field label="Vehicle model" value={f.vehicle_model} onChange={(v) => set("vehicle_model", v)} testid="free-entry-vehicle-model" />
              <Field label="Registration no." value={f.registration_no} onChange={(v) => set("registration_no", v)} testid="free-entry-regno" />
              <Field label="Chassis no." value={f.chassis_no} onChange={(v) => set("chassis_no", v)} testid="free-entry-chassis" />
              <Field label="Serial number" value={f.serial_number} onChange={(v) => set("serial_number", v)} testid="free-entry-serial" />
              <Field label="Area covered (sq. ft.)" type="number" value={f.area_sqft_covered} onChange={(v) => set("area_sqft_covered", v)} testid="free-entry-area" />
              <Field label="Body parts covered" value={f.body_parts_covered} onChange={(v) => set("body_parts_covered", v)} testid="free-entry-bodyparts" />
              <Field label="Certificate no. (blank = auto)" value={f.certificate_number} onChange={(v) => set("certificate_number", v)} testid="free-entry-cert" />
              <Field label="Registration date" required type="date" value={f.start_date} onChange={(v) => set("start_date", v)} testid="free-entry-date" />
              <Field label="Duration (months)" type="number" value={f.duration_months} onChange={(v) => set("duration_months", v)} testid="free-entry-months" />
              <Field label="Plan amount (₹)" type="number" value={f.payment_amount} onChange={(v) => set("payment_amount", v)} testid="free-entry-amount" />
              <div className="sm:col-span-2">
                <Field label="Notes" value={f.notes} onChange={(v) => set("notes", v)} testid="free-entry-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button data-testid="free-entry-submit" disabled={busy} onClick={submit}>
              {busy ? "Saving…" : "Save standalone record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
